import { create } from 'zustand';
import { fetchBusinessCategories } from '@/service/api/businessCategory';

interface BusinessCategoryState {
  /** 已启用的业务类目列表 */
  categories: Api.BusinessCategory.BusinessCategoryResponse[];
  /** 是否正在加载中 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;

  /** 获取已启用业务类目列表 */
  fetchCategories: () => Promise<Api.BusinessCategory.BusinessCategoryResponse[]>;
  /** 根据 code 或 id 获取分类详情对象 */
  getCategoryByCode: (code?: string) => Api.BusinessCategory.BusinessCategoryResponse | undefined;
  /** 根据 code 或 id 获取分类名称 */
  getCategoryNameByCode: (code?: string, fallback?: string) => string;
  /** 清除错误信息 */
  clearError: () => void;
}

let fetchCategoriesPromise: Promise<Api.BusinessCategory.BusinessCategoryResponse[]> | null = null;

export const useBusinessCategoryStore = create<BusinessCategoryState>((set, get) => ({
  categories: [],
  loading: false,
  error: null,

  fetchCategories: async () => {
    const { categories } = get();
    if (categories.length > 0) {
      return categories;
    }
    if (fetchCategoriesPromise) {
      return fetchCategoriesPromise;
    }

    set({ loading: true, error: null });
    fetchCategoriesPromise = fetchBusinessCategories()
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        set({ categories: list, loading: false, error: null });
        return list;
      })
      .catch((err) => {
        const errorMessage = err instanceof Error ? err.message : '获取业务类目列表失败';
        set({ loading: false, error: errorMessage });
        throw err;
      })
      .finally(() => {
        fetchCategoriesPromise = null;
      });

    return fetchCategoriesPromise;
  },

  getCategoryByCode: (code) => {
    if (!code) return undefined;
    const { categories } = get();
    return categories.find((item) => item.code === code || item.id === code);
  },

  getCategoryNameByCode: (code, fallback = '') => {
    if (!code) return fallback;
    const { categories } = get();
    const target = categories.find((item) => item.code === code || item.id === code);
    return target?.name || fallback || code;
  },

  clearError: () => set({ error: null }),
}));

